<!-- OperatorPanel.vue -->
<template>
  <div v-if="!accessVerified" class="body access-error">
    <div class="error-icon">⚠️</div>
    <h3>{{ verificationError || $t('messenger.operatorPanel.checking_access') }}</h3>
    <p v-if="verificationError">{{ $t('messenger.operatorPanel.redirecting') }}</p>
    <div v-else class="loading-spinner"></div>
  </div>

  <div class="body" v-else>
    <div class="operator-panel">
      <Navbar 
        :showBackButton="true" 
        @back="goBack"
      />
      
      <div class="dashboard-grid" style="margin-top: 20px;">
        <!-- Pending Questions -->
        <div class="pending-questions">
          <div class="section-header">
            <h4>{{ $t('messenger.operatorPanel.pending_questions', { count: pendingQuestions.length }) }}</h4>
          </div>
          <div v-if="pendingQuestions.length > 0" class="questions-list">
            <div 
              v-for="(question, index) in pendingQuestions" 
              :key="question.session_id || index"
              class="question-card"
            >
              <div class="question-header">
                <span class="customer-id">
                  {{ $t('messenger.operatorPanel.customer_label') }}: {{ formatCustomerId(question.customer_id) }}
                </span>
                <span class="question-time">{{ formatTime(question.timestamp) }}</span>
              </div>
              <div class="question-content">
                <p class="question-text">{{ question.question }}</p>
                <div class="question-meta">
                  <span class="department-badge" :class="question.department">
                    {{ getDepartmentName(question.department) }}
                  </span>
                  <span class="queue-position" v-if="question.queue_position">
                    {{ $t('messenger.operatorPanel.queue_position') }}: {{ question.queue_position }}
                  </span>
                </div>
              </div>
              <button 
                @click="acceptQuestion(question)"
                class="accept-btn"
                :disabled="operatorStatus !== 'available'"
                :title="operatorStatus !== 'available' ? $t('messenger.operatorPanel.must_be_online') : ''"
              >
                📥 {{ $t('messenger.operatorPanel.accept_question') }}
              </button>
            </div>
          </div>
          <div v-else class="empty-state">
            <span class="empty-icon">📭</span>
            <span>{{ $t('messenger.operatorPanel.no_pending_questions') }}</span>
          </div>
        </div>
        
        <!-- Active Chats -->
        <div class="active-chats">
          <div class="section-header">
            <h4>{{ $t('messenger.operatorPanel.active_chats', { count: activeChats.length }) }}</h4>
          </div>
          <div v-if="activeChats.length > 0" class="chats-list">
            <div 
              v-for="chat in activeChats" 
              :key="chat.chat_id"
              class="chat-card"
            >
              <div class="chat-header">
                <div class="chat-customer">
                  <span class="customer-label">{{ $t('messenger.operatorPanel.customer_label') }}:</span>
                  <span class="customer-id">{{ formatCustomerId(chat.customer_id) }}</span>
                </div>
                <div class="chat-info">
                  <span class="chat-duration">{{ getChatDuration(chat.start_time) }}</span>
                  <span class="chat-messages">{{ chat.message_count || 0 }} {{ $t('messenger.operatorPanel.messages') }}</span>
                </div>
              </div>
              <div class="chat-details">
                <span class="chat-dept">{{ getDepartmentName(chat.department) }}</span>
                <span class="chat-time">{{ $t('messenger.operatorPanel.started_at') }}: {{ formatTime(chat.start_time) }}</span>
              </div>
              <div class="chat-actions">
                <button @click="openChat(chat)" class="action-btn chat">
                  💬 {{ $t('messenger.operatorPanel.continue_chat') }}
                </button>
                <button @click="closeChat(chat.chat_id)" class="action-btn close">
                  ❌ {{ $t('messenger.operatorPanel.end_chat') }}
                </button>
              </div>
            </div>
          </div>
          <div v-else class="empty-state">
            <span class="empty-icon">💬</span>
            <span>{{ $t('messenger.operatorPanel.no_active_chats') }}</span>
          </div>
        </div>
      </div>
      
      <!-- Selected Chat Window -->
      <OperatorChatWindow 
        style="z-index: 99999;"
        v-if="selectedChat"
        :chat-id="selectedChat.chat_id"
        :customer-id="selectedChat.customer_id"
        :department="selectedChat.department"
        :company-id="companyId"
        @close="closeSelectedChat"
        @chat-ended="handleChatEnded"
      />
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useRouter, useRoute } from 'vue-router'
import OperatorChatWindow from './OperatorChatWindow.vue'
import { operatorService } from '@/services/api'
import { messengerService } from '@/services/apiMessanger'
import Navbar from '@/components/Navbar.vue'

export default {
  name: 'OperatorPanel',
  components: {
    OperatorChatWindow,
    Navbar
  },
  props: {
    department: {
      type: String,
      default: 'support'
    },
    companyId: {
      type: [String, Number],
      required: true
    },
    companyName: {
      type: String,
      default: ''
    }
  },
  setup(props) {
    const authStore = useAuthStore()
    const router = useRouter()
    const route = useRoute()
    
    const operatorStatus = ref('available')
    const pendingQuestions = ref([])
    const activeChats = ref([])
    const stats = ref({})
    const selectedChat = ref(null)
    const accessVerified = ref(false)
    const verificationError = ref('')
    
    // Single polling interval instead of multiple
    let pollingInterval = null
    
    // Verify operator access
    const verifyAccess = async () => {
      try {
        const response = await operatorService.checkIfOperator()
        
        if (response.data?.success && response.data.is_operator) {
          const operatorData = response.data.operator_data
          
          if (operatorData.company_id.toString() !== props.companyId.toString()) {
            verificationError.value = 'شما به این شرکت دسترسی ندارید'
            accessVerified.value = false
            setTimeout(() => {
              router.push('/dashboard')
            }, 3000)
            return
          }
          
          const hasCategoryAccess = operatorData.categories?.some(
            cat => cat.type === props.department
          )
          
          if (!hasCategoryAccess && operatorData.categories?.length > 0) {
            const firstCategory = operatorData.categories[0]
            router.replace({
              query: {
                ...route.query,
                department: firstCategory.type
              }
            })
            return
          }
          
          accessVerified.value = true
          
          // Initialize operator in messenger
          await initOperator()
        } else {
          verificationError.value = 'شما به عنوان اپراتور ثبت نشده‌اید'
          accessVerified.value = false
          setTimeout(() => {
            router.push('/dashboard')
          }, 3000)
        }
      } catch (error) {
        console.error('Error verifying access:', error)
        verificationError.value = 'خطا در بررسی دسترسی'
        accessVerified.value = false
      }
    }

    // Add this method to the component
const fetchPendingQuestions = async () => {
  try {
    console.log('🔍 Fetching pending questions for department:', props.department)
    const response = await messengerService.getPendingQuestions(props.department)
    console.log('📊 Pending questions response:', response)
    
    let questions = []
    if (response?.status === 'success' && response?.questions) {
      questions = response.questions
    } else if (Array.isArray(response)) {
      questions = response
    } else if (response?.data?.questions) {
      questions = response.data.questions
    }
    
    console.log('📊 Setting pending questions:', questions.length)
    pendingQuestions.value = questions
  } catch (error) {
    console.error('Error fetching pending questions:', error)
  }
}

const fetchActiveChats = async () => {
  try {
    console.log('🔍 Fetching active chats for operator:', authStore.user?.id)
    const response = await messengerService.getActiveChats(authStore.user?.id)
    console.log('📊 Active chats response:', response)
    
    let chats = []
    if (Array.isArray(response)) {
      chats = response
    } else if (response?.active_chats) {
      chats = response.active_chats
    } else if (response?.data?.active_chats) {
      chats = response.data.active_chats
    }
    
    console.log('📊 Setting active chats:', chats.length)
    activeChats.value = chats
  } catch (error) {
    console.error('Error fetching active chats:', error)
  }
}
    
const initOperator = async () => {
  try {
    const operatorId = String(authStore.user?.id)
    const username = authStore.user?.name || 'Operator'
    
    console.log('🔧 Initializing operator:', { operatorId, username, department: props.department })
    
    const operatorData = await messengerService.registerOperator({
      operator_id: operatorId,
      username: username,
      departments: [props.department],
      company_id: props.companyId,
      public_key: 'temp_operator_key'
    })
    
    console.log('📋 Operator registration response:', operatorData)
    
    if (operatorData.data.status === 'success') {
      // Set initial status
      await updateStatus()
      
      // Start single polling interval for all data
      await fetchPendingQuestions() // Initial load for pending questions
      await fetchActiveChats() // Initial load for active chats
      
      // Start polling
      pollingInterval = setInterval(() => {
        fetchPendingQuestions()
        fetchActiveChats()
      }, 5000) // Poll every 5 seconds
    }
  } catch (error) {
    console.error('Error registering operator:', error)
  }
}
    
    // Update operator status
    const updateStatus = async () => {
      try {
        await messengerService.updateOperatorStatus(
          authStore.user?.id,
          operatorStatus.value
        )
      } catch (error) {
        console.error('Error updating status:', error)
      }
    }
    
    // Single function to refresh all data with change detection
const refreshData = async () => {
  try {
    console.log('🔄 Refreshing data for department:', props.department)
    console.log('🔄 Operator ID:', authStore.user?.id)
    
    // Fetch all data in parallel
    const [questionsRes, chatsRes, statsRes] = await Promise.allSettled([
      messengerService.getPendingQuestions(props.department),
      messengerService.getActiveChats(authStore.user?.id),
      messengerService.getOperatorStats(props.department)
    ])

    console.log('📊 Questions response status:', questionsRes.status)
    
    // Update pending questions
    if (questionsRes.status === 'fulfilled') {
      let questions = []
      const data = questionsRes.value
      
      console.log('📊 Questions response data:', data)
      
      // Handle different response formats
      if (Array.isArray(data)) {
        questions = data
      } else if (data?.data?.questions && Array.isArray(data.data.questions)) {
        questions = data.data.questions
      } else if (data?.questions && Array.isArray(data.questions)) {
        questions = data.questions
      } else if (data?.data && Array.isArray(data.data)) {
        questions = data.data
      } else if (data?.status === 'success' && data?.questions) {
        questions = data.questions
      }
      
      console.log('📊 Parsed questions:', questions.length, questions)
      
      pendingQuestions.value = questions
    }
    
    // Update active chats
    if (chatsRes.status === 'fulfilled') {
      let chats = []
      const data = chatsRes.value
      
      if (Array.isArray(data)) {
        chats = data
      } else if (data?.active_chats && Array.isArray(data.active_chats)) {
        chats = data.active_chats
      } else if (data?.data && Array.isArray(data.data)) {
        chats = data.data
      }
      
      console.log('📊 Active chats:', chats.length)
      activeChats.value = chats
    }
    
    // Update stats
    if (statsRes.status === 'fulfilled') {
      let statsData = {}
      const data = statsRes.value
      
      if (data?.stats) {
        statsData = data.stats
      } else if (data?.data) {
        statsData = data.data
      } else {
        statsData = data
      }
      
      stats.value = statsData
    }
  } catch (error) {
    console.error('Error refreshing data:', error)
  }
}
    
    // Accept question
    const acceptQuestion = async (question) => {
      try {
        const data = await messengerService.acceptQuestion(
          authStore.user?.id,
          question.customer_id,
          props.department
        )
        
        if (data.data.status === 'success') {
          // Immediately update local data
          pendingQuestions.value = pendingQuestions.value.filter(
            q => q.session_id !== question.session_id
          )
          
          // Refresh in background to get the new chat
          setTimeout(refreshData, 500)
        } else {
          alert('خطا در قبول سؤال: ' + (data.error || 'خطای ناشناخته'))
        }
      } catch (error) {
        console.error('Error accepting question:', error)
        alert('خطا در قبول سؤال: ' + error.message)
      }
    }
    
    // Close chat
    const closeChat = async (chatId) => {
      if (!confirm('آیا از پایان دادن به این گفتگو اطمینان دارید؟')) {
        return
      }
      
      try {
        const data = await messengerService.closeChat(chatId)
        
        if (data.status === 'success') {
          // Immediately update local data
          activeChats.value = activeChats.value.filter(c => c.chat_id !== chatId)
          
          if (selectedChat.value && selectedChat.value.chat_id === chatId) {
            selectedChat.value = null
          }
          
          // Refresh in background to sync
          setTimeout(refreshData, 500)
        } else {
          alert('خطا در پایان گفتگو: ' + (data.error || 'خطای ناشناخته'))
        }
      } catch (error) {
        console.error('Error closing chat:', error)
        alert('خطا در پایان گفتگو: ' + error.message)
      }
    }
    
    // Open chat
    const openChat = (chat) => {
      selectedChat.value = chat
    }
    
    // Close selected chat window
    const closeSelectedChat = () => {
      selectedChat.value = null
      refreshData() // Refresh in background
    }
    
    // Handle chat ended
    const handleChatEnded = (chatId) => {
      if (selectedChat.value && selectedChat.value.chat_id === chatId) {
        selectedChat.value = null
      }
      refreshData() // Refresh in background
    }
    
    // Helper methods
    const getDepartmentName = (dept) => {
      const names = {
        support: 'پشتیبانی',
        financial: 'مالی',
        commercial: 'بازرگانی',
        business: 'کسب و کار',
        technical: 'فنی',
        other: 'سایر'
      }
      return names[dept] || dept
    }
    
    const formatTime = (timestamp) => {
      if (!timestamp) return '--:--'
      const date = new Date(timestamp)
      return date.toLocaleTimeString('fa-IR', {
        hour: '2-digit',
        minute: '2-digit'
      })
    }
    
    const formatCustomerId = (customerId) => {
      if (!customerId) return 'ناشناس'
      const str = String(customerId)
      return str.length > 8 ? `${str.slice(0, 8)}...` : str
    }
    
    const getChatDuration = (startTime) => {
      if (!startTime) return '0 دقیقه'
      const start = new Date(startTime)
      const now = new Date()
      const diffMinutes = Math.floor((now - start) / (1000 * 60))
      
      if (diffMinutes < 60) return `${diffMinutes} دقیقه`
      const hours = Math.floor(diffMinutes / 60)
      const minutes = diffMinutes % 60
      return `${hours} ساعت و ${minutes} دقیقه`
    }

    const navigateToDashboard = () => {
      router.push('/dashboard')
    }

    const goBack = () => {
      router.push('/select-role')
    }
    
    // Lifecycle
    onMounted(async () => {
      await verifyAccess()
    })
    
    onUnmounted(() => {
      if (pollingInterval) {
        clearInterval(pollingInterval)
      }
      
      // Set offline status
      operatorStatus.value = 'offline'
      updateStatus().catch(console.error)
    })
    
    return {
      operatorStatus,
      pendingQuestions,
      activeChats,
      stats,
      selectedChat,
      accessVerified,
      verificationError,
      updateStatus,
      acceptQuestion,
      closeChat,
      openChat,
      closeSelectedChat,
      handleChatEnded,
      getDepartmentName,
      formatTime,
      formatCustomerId,
      getChatDuration,
      navigateToDashboard,
      goBack
    }
  }
}
</script>

<style scoped>
/* Operator Panel */
.operator-panel {
  padding: 20px;
  background: var(--bg);
  min-height: 100vh;
}

.operator-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  padding: 20px;
  background: var(--card-bg);
  border-radius: 12px;
  box-shadow: 0 4px 20px var(--shadow-1);
  border: 1px solid var(--card-border);
}

.operator-status {
  display: flex;
  align-items: center;
  gap: 15px;
}

.status-label {
  font-weight: 500;
  color: var(--subtext);
}

.status-select {
  padding: 10px 16px;
  border-radius: 8px;
  border: 2px solid var(--card-border);
  background: var(--card-bg);
  font-size: 14px;
  min-width: 140px;
  cursor: pointer;
  transition: all 0.3s;
  color: var(--text);
}

.status-select:focus {
  outline: none;
  border-color: var(--primary);
  box-shadow: 0 0 0 3px var(--ring);
}

.back-arrow {
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  font-size: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  background: var(--primary);
  color: white;
}

.back-arrow:hover {
  transform: scale(1.1);
  box-shadow: 0 6px 20px var(--ring);
}

.dashboard-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
}

.stats-section {
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-bottom: 10px;
}

.stat-card {
  background: var(--card-bg);
  padding: 24px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  gap: 20px;
  box-shadow: 0 4px 15px var(--shadow-1);
  transition: transform 0.3s, box-shadow 0.3s;
  border: 1px solid var(--card-border);
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px var(--shadow-2);
}

.stat-icon {
  font-size: 36px;
  opacity: 0.8;
}

.stat-info {
  display: flex;
  flex-direction: column;
}

.stat-value {
  display: block;
  font-size: 28px;
  font-weight: bold;
  color: var(--heading);
  line-height: 1.2;
}

.stat-label {
  display: block;
  font-size: 14px;
  color: var(--subtext);
  margin-top: 4px;
}

.pending-questions, .active-chats {
  background: var(--card-bg);
  padding: 24px;
  border-radius: 12px;
  box-shadow: 0 4px 15px var(--shadow-1);
  min-height: 400px;
  display: flex;
  flex-direction: column;
  border: 1px solid var(--card-border);
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 2px solid var(--card-border);
}

.section-header h4 {
  margin: 0;
  font-size: 18px;
  color: var(--heading);
  font-weight: 600;
}

.questions-list, .chats-list {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 15px;
  overflow-y: auto;
  max-height: 500px;
  padding-right: 5px;
}

.question-card, .chat-card {
  border: 1px solid var(--card-border);
  border-radius: 10px;
  padding: 18px;
  background: var(--bg);
  transition: all 0.3s;
}

.question-card:hover, .chat-card:hover {
  border-color: var(--primary);
  background: var(--ring);
  transform: translateX(2px);
}

.question-header, .chat-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
  font-size: 13px;
}

.customer-id {
  color: var(--heading);
  font-weight: 500;
}

.customer-label {
  color: var(--subtext);
  margin-left: 5px;
}

.question-time, .chat-duration {
  color: var(--muted);
}

.question-content {
  margin: 12px 0;
}

.question-text {
  margin: 0 0 12px 0;
  line-height: 1.6;
  color: var(--text);
  font-size: 14px;
}

.question-meta {
  display: flex;
  gap: 10px;
  align-items: center;
}

.department-badge {
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 500;
}

.department-badge.financial {
  background: rgba(16, 185, 129, 0.1);
  color: var(--green);
}

.department-badge.commercial {
  background: rgba(249, 115, 22, 0.1);
  color: var(--orange-1);
}

.department-badge.customer {
  background: rgba(59, 130, 246, 0.1);
  color: var(--primary);
}

.department-badge.technical {
  background: rgba(139, 92, 246, 0.1);
  color: var(--gradient-2);
}

.queue-position {
  font-size: 12px;
  color: var(--subtext);
  padding: 2px 8px;
  background: var(--card-border);
  border-radius: 10px;
}

.accept-btn {
  width: 100%;
  padding: 12px;
  background: linear-gradient(135deg, var(--green) 0%, #2f9e44 100%);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.accept-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #2f9e44 0%, #2b8a3e 100%);
  transform: translateY(-1px);
}

.accept-btn:disabled {
  background: var(--muted);
  cursor: not-allowed;
  opacity: 0.6;
}

.chat-info, .chat-details {
  display: flex;
  gap: 10px;
  align-items: center;
  font-size: 12px;
  color: var(--subtext);
}

.chat-messages {
  padding: 2px 8px;
  background: var(--card-border);
  border-radius: 10px;
}

.chat-dept {
  font-weight: 500;
  color: var(--heading);
}

.chat-time {
}

.chat-actions {
  display: flex;
  gap: 10px;
  margin-top: 15px;
}

.action-btn {
  flex: 1;
  padding: 10px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  font-size: 13px;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.action-btn.chat {
  background: linear-gradient(135deg, var(--primary) 0%, #339af0 100%);
  color: white;
}

.action-btn.chat:hover {
  background: linear-gradient(135deg, #339af0 0%, var(--primary-hover) 100%);
}

.action-btn.close {
  background: linear-gradient(135deg, var(--orange-1) 0%, #e03131 100%);
  color: white;
}

.action-btn.close:hover {
  background: linear-gradient(135deg, #e03131 0%, #c92a2a 100%);
}

.empty-state {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: var(--subtext);
  font-style: italic;
  padding: 40px 20px;
  text-align: center;
}

.empty-icon {
  font-size: 40px;
  margin-bottom: 10px;
  opacity: 0.5;
  color: var(--muted);
}

/* Scrollbar styling */
.questions-list::-webkit-scrollbar,
.chats-list::-webkit-scrollbar {
  width: 6px;
}

.questions-list::-webkit-scrollbar-track,
.chats-list::-webkit-scrollbar-track {
  background: var(--card-border);
  border-radius: 3px;
}

.questions-list::-webkit-scrollbar-thumb,
.chats-list::-webkit-scrollbar-thumb {
  background: var(--muted-2);
  border-radius: 3px;
}

.questions-list::-webkit-scrollbar-thumb:hover,
.chats-list::-webkit-scrollbar-thumb:hover {
  background: var(--muted);
}

/* Access Error */
.access-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  text-align: center;
  padding: 40px;
  background: var(--card-bg);
  border-radius: 12px;
  margin: 20px;
  box-shadow: 0 4px 12px var(--shadow-1);
  border: 1px solid var(--card-border);
}

.error-icon {
  font-size: 48px;
  margin-bottom: 20px;
}

.access-error h3 {
  color: var(--orange-1);
  margin-bottom: 10px;
}

.access-error p {
  color: var(--subtext);
}

/* Loading Spinner */
.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid var(--card-border);
  border-top: 3px solid var(--primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-top: 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Responsive */
@media (max-width: 1200px) {
  .dashboard-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .operator-header {
    flex-direction: column;
    gap: 15px;
    text-align: center;
  }
  
  .stats-section {
    grid-template-columns: 1fr;
  }
  
  .chat-actions {
    flex-direction: column;
  }
}
</style>